import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Briefcase,
  GraduationCap,
  Download,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PROFILE, EXPERIENCE, EDUCATION, COMPETENCIES } from "@/data/profile";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ArticleCard from "@/components/site/ArticleCard";
import SEO from "@/components/site/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.06 },
  }),
};

export default function HomePage() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api
      .get("/articles")
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]));
  }, []);

  return (
    <div data-testid="page-home" className="bg-paper">
      <SEO
        title="Executive Portfolio & Insights"
        description="Nimisha Mohan Srivastava — enterprise-scale strategy and operations leader with 14+ years across Amazon, Reliance Jio, Zoomcar, Capital One and Wipro. Associate Director, AI Native Practice at Wipro."
        image="https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg"
        type="profile"
      />
      <Navbar variant="dark" />

      {/* HERO */}
      <section
        data-testid="section-hero"
        className="relative bg-navy text-paper min-h-screen flex items-end overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-20 mix-blend-luminosity pointer-events-none"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,28,44,0.9) 0%, rgba(5,28,44,0.6) 40%, rgba(5,28,44,0.95) 100%)",
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-52 sm:pb-56 md:py-40 md:pb-40 grid grid-cols-1 md:grid-cols-12 gap-8 items-end w-full">
          <div className="md:col-span-9">
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="eyebrow text-gold mb-6 md:mb-8"
              data-testid="hero-eyebrow"
            >
              Executive profile · {PROFILE.currentRole}
            </motion.p>

            <motion.h1
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate="show"
              className="font-serif text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] tracking-tight text-paper mb-8 md:mb-10 max-w-5xl"
              data-testid="hero-headline"
            >
              Building enterprise-scale value across{" "}
              <span className="text-gold italic">strategy, operations</span> and
              P&L.
            </motion.h1>

            <motion.div
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate="show"
              className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 text-paper/70 text-sm mb-12 sm:mb-16"
            >
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" strokeWidth={1.5} />
                {PROFILE.location}
              </span>
              <span className="hidden sm:block text-paper/30">/</span>
              <span className="hidden md:block">
                14+ years across Amazon, Reliance Jio, Zoomcar, Capital One, Wipro
              </span>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              animate="show"
              className="flex flex-wrap items-center gap-4"
            >
              <a
                href="/Nimisha_Mohan_Srivastava_Resume.pdf"
                download="Nimisha_Mohan_Srivastava_Resume.pdf"
                data-testid="btn-download-resume"
                className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-navy px-6 py-3.5 font-medium transition-colors"
              >
                <Download className="w-4 h-4" strokeWidth={2} />
                <span className="eyebrow">Download resume</span>
              </a>
              <Link
                to="/insights"
                data-testid="btn-hero-insights"
                className="group inline-flex items-center gap-3 border border-paper/30 hover:border-gold text-paper hover:text-gold px-6 py-3.5 transition-colors"
              >
                <span className="eyebrow">Read insights</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>

          <div className="md:col-span-3 md:col-start-10 pb-2 hidden md:block">
            <motion.a
              variants={fadeUp}
              custom={4}
              initial="hidden"
              animate="show"
              href="#experience"
              data-testid="btn-scroll-experience"
              className="group inline-flex items-center gap-3 eyebrow text-paper hover:text-gold transition-colors border-b border-paper/30 hover:border-gold pb-2"
            >
              Scroll for profile
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </motion.a>
          </div>
        </div>

        {/* Metrics ribbon */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-paper/10 bg-navy/60 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 md:py-6 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {PROFILE.headlineMetrics.map((m, i) => (
              <motion.div
                key={m.label}
                variants={fadeUp}
                custom={i + 5}
                initial="hidden"
                animate="show"
                data-testid={`hero-metric-${i}`}
              >
                <p className="font-serif text-2xl sm:text-3xl md:text-4xl text-gold">
                  {m.value}
                </p>
                <p className="eyebrow text-paper/60 mt-1 text-[10px] sm:text-xs">
                  {m.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / ACTION TITLE */}
      <section
        data-testid="section-about"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8"
      >
        <div className="md:col-span-4">
          <p className="eyebrow text-gold mb-6">01 · About</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
            From factory floor to Fortune 500 boardroom.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-6 text-ink-muted text-lg leading-relaxed">
          <p>{PROFILE.summary}</p>
          <p>
            Michigan Ross MBA, NIT Warangal B.Tech (Honors), and today
            Associate Director of the AI Native practice at Wipro — defining the
            practice roadmap, solutions engineering and strategy that take
            enterprise AI from pilot to production at scale.
          </p>
        </div>
      </section>

      {/* EXPERIENCE TIMELINE */}
      <section
        id="experience"
        data-testid="section-experience"
        className="bg-cream border-y border-rule py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <p className="eyebrow text-gold mb-6">02 · Experience</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
                Fourteen years, five industries, one throughline: value creation
                at scale.
              </h2>
            </div>
          </div>

          <div className="relative">
            {/* vertical rule: left on mobile, aligned at 16.66% on md+ */}
            <div className="absolute left-3 md:left-[16.66%] top-0 bottom-0 w-px bg-rule" />

            <div className="space-y-12 md:space-y-16">
              {EXPERIENCE.map((exp, i) => (
                <motion.div
                  key={`${exp.company}-${exp.dates}`}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-80px" }}
                  custom={i}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 relative pl-10 md:pl-0"
                  data-testid={`experience-item-${i}`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-[6px] md:left-[16.66%] md:-ml-[7px] top-2 w-3.5 h-3.5 bg-paper border border-gold rounded-none">
                    <div className="absolute inset-[3px] bg-navy" />
                  </div>

                  {/* Dates column */}
                  <div className="md:col-span-2 md:pl-6">
                    <p className="eyebrow text-navy">{exp.dates}</p>
                    {exp.isCurrent && (
                      <p className="eyebrow text-gold mt-1">· Current</p>
                    )}
                  </div>

                  {/* Role + bullets column */}
                  <div className="md:col-span-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase
                        className="w-3.5 h-3.5 text-gold"
                        strokeWidth={1.5}
                      />
                      <p className="font-serif text-xs sm:text-sm text-ink-muted">
                        {exp.location}
                      </p>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight text-navy mb-1">
                      {exp.role}
                    </h3>
                    <p className="text-base sm:text-lg text-ink-muted mb-4">
                      {exp.company}
                    </p>
                    <ul className="space-y-3">
                      {exp.bullets.map((b, bi) => (
                        <li
                          key={bi}
                          className="pl-5 relative text-ink-muted text-sm sm:text-[15px] leading-relaxed"
                        >
                          <span className="absolute left-0 top-[0.75em] w-3 h-px bg-gold" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Impact metrics column */}
                  <div className="md:col-span-4">
                    <p className="eyebrow text-ink-faint mb-3">
                      Impact metrics
                    </p>
                    <div className="flex flex-wrap md:flex-col gap-2">
                      {exp.metrics.map((m) => (
                        <div
                          key={m}
                          className="border border-navy/10 bg-paper px-3 md:px-4 py-2 md:py-2.5 text-xs sm:text-sm text-navy"
                        >
                          {m}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPETENCIES */}
      <section
        data-testid="section-competencies"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8"
      >
        <div className="md:col-span-4">
          <p className="eyebrow text-gold mb-6">03 · Core competencies</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
            Where I compound value.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-rule border border-rule">
            {COMPETENCIES.map((c, i) => (
              <motion.div
                key={c}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                custom={i}
                className="bg-paper p-6 hover:bg-navy hover:text-paper transition-colors group cursor-default"
                data-testid={`competency-${i}`}
              >
                <p className="eyebrow text-gold group-hover:text-gold mb-2">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="font-serif text-lg leading-tight text-navy group-hover:text-paper transition-colors">
                  {c}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EDUCATION */}
      <section
        data-testid="section-education"
        className="bg-cream border-y border-rule py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <p className="eyebrow text-gold mb-6">04 · Education</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
              Trained where rigour meets ambition.
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-10">
            {EDUCATION.map((edu, i) => (
              <div
                key={edu.school}
                className="border-l-2 border-gold pl-6"
                data-testid={`education-item-${i}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <GraduationCap
                    className="w-4 h-4 text-gold"
                    strokeWidth={1.5}
                  />
                  <p className="eyebrow text-ink-muted">
                    {edu.date} · {edu.location}
                  </p>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight text-navy mb-2">
                  {edu.school}
                </h3>
                <p className="text-lg text-ink-muted mb-3">{edu.degree}</p>
                <p className="text-sm text-ink-muted leading-relaxed max-w-2xl">
                  {edu.notes}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section
        data-testid="section-insights"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 items-end">
          <div className="md:col-span-8">
            <p className="eyebrow text-gold mb-6">05 · Insights</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-navy">
              Long-form strategy notes, from marketplaces to orbit.
            </h2>
          </div>
          <div className="md:col-span-3 md:col-start-10 flex md:justify-end">
            <Link
              to="/insights"
              data-testid="btn-view-all-insights"
              className="group inline-flex items-center gap-3 eyebrow text-navy hover:text-gold transition-colors border-b border-navy hover:border-gold pb-1"
            >
              View all
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.slice(0, 3).map((a, i) => (
            <ArticleCard key={a.id} article={a} index={i} />
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
